import { useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import CreateAssetModalContext from '@atom/components/common/createAssetModal/CreateAssetModalContext';
import { ASSET_ROUTE_AUTOCOMPLETE } from '@atom/graph/asset';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import {
  buildSimpleInput,
  computeAutoFillUpdates,
  mapAutocompleteToOptions,
} from './assetRouteUtilities';
import MilePostInput from './MilePostInput';
import RouteSelectInput from './RouteSelectInput';

import modalClasses from '../createAssetModal.module.css';
import classes from './assetRouteTab.module.css';

const AssetRouteTab = () => {
  const {
    location,
    routeData,
    setRouteData,
    routeOptions,
    setRouteOptions,
    milePost,
    setMilePost,
    milePostRanges,
    setMilePostRanges,
  } = useContext(CreateAssetModalContext);

  const [runAutocomplete, { loading }] = useMutation(ASSET_ROUTE_AUTOCOMPLETE);

  useEffect(() => {
    const getAutocomplete = async () => {
      const input = buildSimpleInput(location, routeData);
      const res = await runAutocomplete({ variables: { input } });
      const data = res?.data?.assetRouteAutocomplete;

      setRouteOptions(mapAutocompleteToOptions(data));
      setMilePostRanges(data?.milePostRanges || []);

      const updates = computeAutoFillUpdates(routeData, data?.components);
      if (!isNilOrEmpty(updates)) {
        setRouteData({ ...routeData, ...updates });
      }
    };

    if (location) {
      getAutocomplete();
    }
  }, [location, routeData]);

  const updateRouteValue = (title: string, value: string | null) => {
    if (isNilOrEmpty(value)) {
      setRouteData(R.map(() => null, routeData));
      setMilePost(null);
      return;
    }

    setRouteData({ ...routeData, [title]: value });
    setMilePost(null);
  };

  const titles = R.keys(routeOptions);
  const milePostDisabled = loading || !milePostRanges.length;

  return (
    <div className={modalClasses.tabContainer}>
      <div className={modalClasses.tabTitle}>Location Details</div>
      <div className={classes.inputSection}>
        {titles.map((title: string) => (
          <RouteSelectInput
            key={title}
            title={title}
            value={routeData[title]}
            options={routeOptions[title]?.enumeration || []}
            onChange={updateRouteValue}
            loading={loading}
          />
        ))}
        <MilePostInput
          value={milePost}
          ranges={milePostRanges}
          onChange={setMilePost}
          disabled={milePostDisabled}
        />
      </div>
    </div>
  );
};

export default AssetRouteTab;
